// Diffs the extractor payload (term-sheet extractor in containers[0].data) against the deal form for the Publish-to-Benchmark Review step.
// Two-key model: extractor emits `c1.*` / `t.*`; deal form uses `col.*` / `l.*` (post-remap, see term-sheet-review-dialog `remapAliases`).

import { BaseEnumValue, SchemaProperty } from '@qtek/shared/models';
import {
  formatTermSheetValue,
  TermSheetFieldType,
} from '../current-deal-entry-method-dialog/term-sheet-extraction.utils';

export type FieldSelection = 'current' | 'document';

interface FieldDef {
  extractedKey: string;
  /** Override when the deal form path differs from the extractor key. */
  formKey?: string;
  label: string;
  type: TermSheetFieldType;
}

export interface ConflictRow {
  /** Deal-form path (used to patch the form on confirm). */
  key: string;
  label: string;
  type: TermSheetFieldType;
  currentDisplay: string;
  documentDisplay: string;
  documentRaw: string;
  isCurrentEmpty: boolean;
  selection: FieldSelection;
}

export interface ConflictReview {
  rows: ConflictRow[];
  conflictCount: number;
  emptyCount: number;
}

const SECTIONS: { title: string; fields: FieldDef[] }[] = [
  {
    title: 'Deal Info',
    fields: [
      { extractedKey: 'c.pr', label: 'Product', type: 'string' },
      { extractedKey: 'c.sTp', label: 'Transaction Type', type: 'string' },
      { extractedKey: 'c.uTp', label: 'Underwriting Type', type: 'string' },
      { extractedKey: 'c.loanPurpose', label: 'Loan Purpose', type: 'string' },
      { extractedKey: 'c.indu', label: 'Industry', type: 'string' },
      {
        extractedKey: 'c.usState',
        label: 'US State or Territory',
        type: 'string',
      },
      { extractedKey: 'c.projectType', label: 'Project Type', type: 'string' },
      {
        extractedKey: 'c.crePropertyClass',
        label: 'Property Class',
        type: 'string',
      },
      {
        extractedKey: 'b.privateEquitySponsored',
        label: 'Private Equity Sponsored',
        type: 'string',
      },
      { extractedKey: 'b.naics', label: 'NAICS Code', type: 'string' },
      {
        extractedKey: 'b.borrowerRevenueSize',
        label: 'Revenue Size',
        type: 'string',
      },
      { extractedKey: 'b.ebitda', label: 'EBITDA', type: 'number' },
      {
        extractedKey: 'b.leverageRatio',
        label: 'Leverage Ratio',
        type: 'number',
      },
    ],
  },
  {
    title: 'Facility Structure',
    fields: [
      {
        extractedKey: 'c.numberOfLenders',
        label: 'Number of Lenders',
        type: 'number',
      },
      {
        extractedKey: 'c.oAmt',
        label: 'Total Facility Amount',
        type: 'number',
      },
      { extractedKey: 'c.trm', label: 'Term (Months)', type: 'number' },
      {
        extractedKey: 'c.amtz',
        label: 'Amortization (Months)',
        type: 'number',
      },
      // Extractor emits `t.iop`; form stores it as `l.iop` (post-remap).
      {
        extractedKey: 't.iop',
        formKey: 'l.iop',
        label: 'Interest Only Period (Months)',
        type: 'number',
      },
      {
        extractedKey: 'd.drawPrd',
        label: 'Draw Period (Months)',
        type: 'number',
      },
      {
        extractedKey: 'c.pmtFrq',
        label: 'Payment Frequency',
        type: 'string',
      },
      {
        extractedKey: 'c.ddConv',
        label: 'Day Count Convention',
        type: 'string',
      },
      { extractedKey: 'c.launchDt', label: 'Launch Date', type: 'date' },
      { extractedKey: 'c.mandateDt', label: 'Mandate Date', type: 'date' },
      { extractedKey: 'c.commitDt', label: 'Commitment Date', type: 'date' },
      { extractedKey: 'c.allocDt', label: 'Allocation Date', type: 'date' },
      { extractedKey: 'c.cDt', label: 'Closed Date', type: 'date' },
      { extractedKey: 'c.maturityDt', label: 'Maturity Date', type: 'date' },
    ],
  },
  {
    title: 'Pricing',
    fields: [
      {
        extractedKey: 'f.interestRateType',
        label: 'Interest Rate Type',
        type: 'string',
      },
      { extractedKey: 'c.indx', label: 'Index', type: 'string' },
      { extractedKey: 'c.sprd', label: 'Spread', type: 'percent' },
      { extractedKey: 'c.pp', label: 'Purchase Price', type: 'percent' },
      {
        extractedKey: 'c.initInrRte',
        label: 'Initial Interest Rate',
        type: 'percent',
      },
      {
        extractedKey: 'c.rrf',
        label: 'Rate Reset Frequency',
        type: 'string',
      },
      {
        extractedKey: 'f.interestRateFloorType',
        label: 'Interest Rate Floor Type',
        type: 'string',
      },
      {
        extractedKey: 'f.whatIsTheFloorRate',
        label: 'Floor Rate',
        type: 'percent',
      },
    ],
  },
  {
    title: 'Fees',
    fields: [
      { extractedKey: 'f.origFee', label: 'Origination Fee', type: 'percent' },
      { extractedKey: 'f.aFee', label: 'Arrangement Fee', type: 'percent' },
      {
        extractedKey: 'd.unFee',
        formKey: 's.unFee',
        label: 'Unused Fee',
        type: 'percent',
      },
      {
        extractedKey: 'd.aRte',
        formKey: 's.aRte',
        label: 'Advance Rate',
        type: 'percent',
      },
      { extractedKey: 'd.unBlnc', label: 'Unused Balance', type: 'number' },
    ],
  },
  {
    title: 'Collateral & Covenants',
    fields: [
      // c1.* comes off the extractor; deal form stores these as col.*
      {
        extractedKey: 'c1.loanToValue',
        formKey: 'col.loanToValue',
        label: 'Loan to Value',
        type: 'percent',
      },
      {
        extractedKey: 'c1.loanToCost',
        formKey: 'col.loanToCost',
        label: 'Loan to Cost',
        type: 'percent',
      },
      {
        extractedKey: 'c1.debtYield',
        formKey: 'col.debtYield',
        label: 'Debt Yield',
        type: 'percent',
      },
      {
        extractedKey: 'c1.recourseLevel',
        formKey: 'col.recourseLevel',
        label: 'Recourse Level',
        type: 'string',
      },
    ],
  },
];

const FIELD_DEFS: FieldDef[] = SECTIONS.flatMap(s => s.fields);

const isEmpty = (raw: unknown): boolean => {
  if (raw === null || raw === undefined) return true;
  const trimmed = String(raw).trim();
  return trimmed === '' || trimmed.toLowerCase() === 'null';
};

// Form stores percents as decimals (5.75% → 0.0575); normalize before formatting.
const formatCurrentValue = (raw: unknown, type: TermSheetFieldType): string => {
  if (isEmpty(raw)) return '';
  if (type === 'percent') {
    const n = Number(raw);
    if (Number.isFinite(n)) {
      return formatTermSheetValue(String(n * 100), 'percent');
    }
  }
  if (type === 'string') {
    const s = String(raw).trim();
    if (SCHEMA_ID_RE.test(s)) return humanizeSchemaId(s);
  }
  return formatTermSheetValue(String(raw), type);
};

/** Document-side display: same schema-ID humanization for string fields. */
const formatDocumentValue = (
  raw: unknown,
  type: TermSheetFieldType
): string => {
  const normalizedRaw = String(raw);
  if (type === 'string') {
    const s = normalizedRaw.trim();
    if (SCHEMA_ID_RE.test(s)) return humanizeSchemaId(s);
  }
  return formatTermSheetValue(normalizedRaw, type);
};

const getEnumOptionLabel = (
  property: SchemaProperty | null | undefined,
  raw: string
): string | null => {
  const options = Array.isArray(property?.v)
    ? (property.v as BaseEnumValue[])
    : [];
  const match = options.find(option => String(option.id ?? '') === raw);

  return match ? (match.description ?? match.label ?? null) : null;
};

const formatReviewValue = (
  raw: unknown,
  type: TermSheetFieldType,
  property: SchemaProperty | null | undefined,
  formatter: (raw: unknown, type: TermSheetFieldType) => string
): string => {
  if (isEmpty(raw)) return '';

  if (type === 'string') {
    const enumLabel = getEnumOptionLabel(property, String(raw).trim());
    if (enumLabel) {
      return enumLabel;
    }
  }

  return formatter(raw, type);
};

const parseComparablePercent = (raw: string): number | null => {
  const normalized = raw.replace(/%/g, '').replace(/,/g, '').trim();
  if (!normalized) {
    return null;
  }

  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
};

const hasEquivalentValues = (
  type: TermSheetFieldType,
  currentDisplay: string,
  documentDisplay: string
): boolean => {
  if (type === 'percent') {
    const currentPercent = parseComparablePercent(currentDisplay);
    const documentPercent = parseComparablePercent(documentDisplay);

    if (currentPercent !== null && documentPercent !== null) {
      return Math.abs(currentPercent - documentPercent) < 1e-9;
    }
  }

  return currentDisplay === documentDisplay;
};

// Deal form is flat-keyed with literal dotted strings (item-dialog.component.ts:initForm); try literal key first, fall back to nested traversal.
const readNested = (
  values: Record<string, unknown> | null | undefined,
  path: string
): unknown => {
  if (!values) return undefined;
  if (Object.prototype.hasOwnProperty.call(values, path)) {
    return values[path];
  }
  return path.split('.').reduce<unknown>((acc, segment) => {
    if (acc === null || acc === undefined) return acc;
    return (acc as Record<string, unknown>)[segment];
  }, values);
};

// Renders schema IDs (e.g. `io.synply.v1.model.product.lineOfCredit` → "Line Of Credit"); underlying value stays the schema ID.
const SCHEMA_ID_RE = /^io\.synply\.v\d+\.[\w.]+$/;
const humanizeSchemaId = (raw: string): string => {
  const last = raw.split('.').pop() ?? raw;
  return last
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, c => c.toUpperCase());
};

export interface BuildConflictReviewArgs {
  /** Backend extraction payload (containers[0].data). */
  extracted: Record<string, string | null>;
  /** Merged form snapshot (form + rootForm raw values). */
  currentValues: Record<string, unknown> | null;
  /** Flat schema properties keyed by dotted form path. */
  properties?: Record<string, SchemaProperty> | null;
}

export const buildConflictReview = ({
  extracted,
  currentValues,
  properties,
}: BuildConflictReviewArgs): ConflictReview => {
  const rows: ConflictRow[] = [];
  let conflictCount = 0;
  let emptyCount = 0;

  for (const def of FIELD_DEFS) {
    const docRaw = extracted[def.extractedKey];
    if (isEmpty(docRaw)) continue;

    const formKey = def.formKey ?? def.extractedKey;
    const currentRaw = readNested(currentValues, formKey);
    const isCurrentEmpty = isEmpty(currentRaw);
    const property = properties?.[formKey];

    const currentDisplay = formatReviewValue(
      currentRaw,
      def.type,
      property,
      formatCurrentValue
    );
    const documentDisplay = formatReviewValue(
      String(docRaw),
      def.type,
      property,
      formatDocumentValue
    );

    if (
      !isCurrentEmpty &&
      hasEquivalentValues(def.type, currentDisplay, documentDisplay)
    ) {
      continue;
    }

    rows.push({
      key: formKey,
      label: def.label,
      type: def.type,
      currentDisplay,
      documentDisplay,
      documentRaw: String(docRaw).trim(),
      isCurrentEmpty,
      selection: isCurrentEmpty ? 'document' : 'current',
    });

    if (isCurrentEmpty) emptyCount++;
    else conflictCount++;
  }

  return { rows, conflictCount, emptyCount };
};

// Parses an extracted string into the form's expected type; percents become decimals (5.75% → 0.0575).
export const parseFieldValue = (
  raw: string,
  type: TermSheetFieldType
): unknown => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  switch (type) {
    case 'number': {
      const n = Number(trimmed.replace(/,/g, ''));
      return Number.isFinite(n) ? n : trimmed;
    }
    case 'percent': {
      const cleaned = trimmed.replace(/%/g, '').replace(/,/g, '').trim();
      const n = Number(cleaned);
      return Number.isFinite(n) ? n / 100 : trimmed;
    }
    case 'date':
    case 'string':
    default:
      return trimmed;
  }
};
